/** Shared kinds, statuses, and parse/validate for Ops Obligations (OB-1). */

import {
  mergeObligationDetails,
  parseObligationDetails,
  type OpsObligationDetails,
} from "./opsObligationDetails.js";

export type { OpsObligationDetails };
export { mergeObligationDetails, parseObligationDetails };

export const OPS_OBLIGATION_KINDS = [
  "utility",
  "internet",
  "lease",
  "mortgage",
  "other",
] as const;
export type OpsObligationKind = (typeof OPS_OBLIGATION_KINDS)[number];

export const OPS_UTILITY_SUBTYPES = [
  "electricity",
  "water",
  "sewer",
  "gas",
  "trash",
  "other",
] as const;
export type OpsUtilitySubtype = (typeof OPS_UTILITY_SUBTYPES)[number];

export const OPS_OBLIGATION_STATUSES = ["active", "ended", "draft"] as const;
export type OpsObligationStatus = (typeof OPS_OBLIGATION_STATUSES)[number];

export const OPS_OBLIGATION_CADENCES = [
  "monthly",
  "quarterly",
  "yearly",
  "weekly",
  "other",
] as const;
export type OpsObligationCadence = (typeof OPS_OBLIGATION_CADENCES)[number];

const KIND_SET = new Set<string>(OPS_OBLIGATION_KINDS);
const SUBTYPE_SET = new Set<string>(OPS_UTILITY_SUBTYPES);
const STATUS_SET = new Set<string>(OPS_OBLIGATION_STATUSES);
const CADENCE_SET = new Set<string>(OPS_OBLIGATION_CADENCES);

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export const OPS_OBLIGATION_KIND_LABELS: Record<OpsObligationKind, string> = {
  utility: "Utility",
  internet: "Internet / telecom",
  lease: "Lease",
  mortgage: "Mortgage",
  other: "Other",
};

export const OPS_UTILITY_SUBTYPE_LABELS: Record<OpsUtilitySubtype, string> = {
  electricity: "Electricity",
  water: "Water",
  sewer: "Sewer",
  gas: "Natural gas",
  trash: "Trash",
  other: "Other utility",
};

export function isOpsObligationKind(v: unknown): v is OpsObligationKind {
  return typeof v === "string" && KIND_SET.has(v);
}
export function isOpsUtilitySubtype(v: unknown): v is OpsUtilitySubtype {
  return typeof v === "string" && SUBTYPE_SET.has(v);
}
export function isOpsObligationStatus(v: unknown): v is OpsObligationStatus {
  return typeof v === "string" && STATUS_SET.has(v);
}
export function isOpsObligationCadence(v: unknown): v is OpsObligationCadence {
  return typeof v === "string" && CADENCE_SET.has(v);
}

export function centsToDollarInput(cents: number | null | undefined): string {
  if (cents == null) return "";
  return (cents / 100).toFixed(2);
}

export function dollarInputToCents(raw: string): number | null {
  const t = raw.trim();
  if (!t) return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

function asTrimmed(v: unknown, max: number): string | null {
  if (typeof v !== "string") return null;
  const t = v.trim();
  if (t.length > max) return null;
  return t;
}

function asOptionalText(v: unknown, max: number): { ok: true; value: string } | { ok: false; error: string } {
  if (v === undefined || v === null) return { ok: true, value: "" };
  if (typeof v !== "string") return { ok: false, error: "must be a string" };
  const t = v.trim();
  if (t.length > max) return { ok: false, error: `too long (max ${max})` };
  return { ok: true, value: t };
}

function asOptionalUuid(v: unknown): { ok: true; value: string | null } | { ok: false; error: string } {
  if (v === undefined || v === null || v === "") return { ok: true, value: null };
  if (typeof v !== "string" || !UUID_RE.test(v.trim())) {
    return { ok: false, error: "must be a UUID or empty" };
  }
  return { ok: true, value: v.trim().toLowerCase() };
}

function asOptionalDate(v: unknown): { ok: true; value: string | null } | { ok: false; error: string } {
  if (v === undefined || v === null || v === "") return { ok: true, value: null };
  if (typeof v !== "string" || !DATE_RE.test(v.trim())) {
    return { ok: false, error: "must be YYYY-MM-DD or empty" };
  }
  return { ok: true, value: v.trim() };
}

function asOptionalCents(v: unknown): { ok: true; value: number | null } | { ok: false; error: string } {
  if (v === undefined || v === null || v === "") return { ok: true, value: null };
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) {
    return { ok: false, error: "must be a non-negative integer (cents)" };
  }
  return { ok: true, value: n };
}

function asPaymentUrl(v: unknown): { ok: true; value: string | null } | { ok: false; error: string } {
  if (v === undefined || v === null || v === "") return { ok: true, value: null };
  if (typeof v !== "string") return { ok: false, error: "payment_url must be a string" };
  const t = v.trim();
  if (!t) return { ok: true, value: null };
  if (t.length > 500) return { ok: false, error: "payment_url too long (max 500)" };
  if (!/^https?:\/\//i.test(t)) return { ok: false, error: "payment_url must start with http:// or https://" };
  return { ok: true, value: t };
}

export type OpsObligationDraft = {
  kind: OpsObligationKind;
  utility_subtype: OpsUtilitySubtype | null;
  location_id: string | null;
  provider_name: string;
  account_ref: string;
  description: string;
  payment_url: string | null;
  cadence: OpsObligationCadence;
  due_rule: string;
  amount_cents: number | null;
  responsible_party: string;
  provider_contact: string;
  status: OpsObligationStatus;
  starts_on: string | null;
  ends_on: string | null;
  notes: string;
  details: OpsObligationDetails;
};

export type ParseResult<T> = { ok: true; value: T } | { ok: false; error: string };

function parseKindAndSubtype(
  body: Record<string, unknown>,
  existingKind?: OpsObligationKind,
): ParseResult<{ kind: OpsObligationKind; utility_subtype: OpsUtilitySubtype | null }> {
  const kindRaw = body.kind;
  const kind = kindRaw === undefined && existingKind ? existingKind : kindRaw;
  if (!isOpsObligationKind(kind)) return { ok: false, error: "kind is required" };
  let utility_subtype: OpsUtilitySubtype | null = null;
  if (kind === "utility") {
    if (body.utility_subtype === undefined && existingKind === "utility") {
      return { ok: true, value: { kind, utility_subtype: null } };
    }
    if (body.utility_subtype === undefined || body.utility_subtype === null || body.utility_subtype === "") {
      utility_subtype = null;
    } else if (isOpsUtilitySubtype(body.utility_subtype)) {
      utility_subtype = body.utility_subtype;
    } else {
      return { ok: false, error: "utility_subtype invalid" };
    }
  }
  return { ok: true, value: { kind, utility_subtype } };
}

export function parseObligationCreate(body: Record<string, unknown>): ParseResult<OpsObligationDraft> {
  const ks = parseKindAndSubtype(body);
  if (!ks.ok) return ks;
  const provider = asTrimmed(body.provider_name, 200);
  if (provider === null && typeof body.provider_name === "string" && body.provider_name.trim().length > 200) {
    return { ok: false, error: "provider_name too long (max 200)" };
  }
  const loc = asOptionalUuid(body.location_id);
  if (!loc.ok) return { ok: false, error: `location_id ${loc.error}` };
  const pay = asPaymentUrl(body.payment_url);
  if (!pay.ok) return { ok: false, error: pay.error };
  const cadence = body.cadence === undefined || body.cadence === "" ? "monthly" : body.cadence;
  if (!isOpsObligationCadence(cadence)) return { ok: false, error: "cadence invalid" };
  const status = body.status === undefined || body.status === "" ? "active" : body.status;
  if (!isOpsObligationStatus(status)) return { ok: false, error: "status invalid" };
  const cents = asOptionalCents(body.amount_cents);
  if (!cents.ok) return { ok: false, error: `amount_cents ${cents.error}` };
  const starts = asOptionalDate(body.starts_on);
  if (!starts.ok) return { ok: false, error: `starts_on ${starts.error}` };
  const ends = asOptionalDate(body.ends_on);
  if (!ends.ok) return { ok: false, error: `ends_on ${ends.error}` };

  const account = asOptionalText(body.account_ref, 120);
  if (!account.ok) return { ok: false, error: `account_ref ${account.error}` };
  const description = asOptionalText(body.description, 2000);
  if (!description.ok) return { ok: false, error: `description ${description.error}` };
  const due = asOptionalText(body.due_rule, 200);
  if (!due.ok) return { ok: false, error: `due_rule ${due.error}` };
  const responsible = asOptionalText(body.responsible_party, 200);
  if (!responsible.ok) return { ok: false, error: `responsible_party ${responsible.error}` };
  const contact = asOptionalText(body.provider_contact, 500);
  if (!contact.ok) return { ok: false, error: `provider_contact ${contact.error}` };
  const notes = asOptionalText(body.notes, 8000);
  if (!notes.ok) return { ok: false, error: `notes ${notes.error}` };

  return {
    ok: true,
    value: {
      kind: ks.value.kind,
      utility_subtype: ks.value.utility_subtype,
      location_id: loc.value,
      provider_name: provider ?? "",
      account_ref: account.value,
      description: description.value,
      payment_url: pay.value,
      cadence,
      due_rule: due.value,
      amount_cents: cents.value,
      responsible_party: responsible.value,
      provider_contact: contact.value,
      status,
      starts_on: starts.value,
      ends_on: ends.value,
      notes: notes.value,
      details: parseObligationDetails(body.details),
    },
  };
}

export type OpsObligationPatch = Partial<OpsObligationDraft>;

export function parseObligationPatch(
  body: Record<string, unknown>,
  existingKind: OpsObligationKind,
): ParseResult<OpsObligationPatch> {
  const patch: OpsObligationPatch = {};
  if (body.kind !== undefined || body.utility_subtype !== undefined) {
    const ks = parseKindAndSubtype(body, existingKind);
    if (!ks.ok) return ks;
    if (body.kind !== undefined) patch.kind = ks.value.kind;
    const nextKind = patch.kind ?? existingKind;
    patch.utility_subtype = nextKind === "utility" ? ks.value.utility_subtype : null;
    if (body.kind !== undefined && nextKind !== "utility") patch.utility_subtype = null;
  }
  if (body.provider_name !== undefined) {
    const n = asTrimmed(body.provider_name, 200);
    if (n === null && typeof body.provider_name === "string" && body.provider_name.trim().length > 200) {
      return { ok: false, error: "provider_name too long (max 200)" };
    }
    patch.provider_name = n ?? "";
  }
  if (body.location_id !== undefined) {
    const loc = asOptionalUuid(body.location_id);
    if (!loc.ok) return { ok: false, error: `location_id ${loc.error}` };
    patch.location_id = loc.value;
  }
  if (body.payment_url !== undefined) {
    const pay = asPaymentUrl(body.payment_url);
    if (!pay.ok) return { ok: false, error: pay.error };
    patch.payment_url = pay.value;
  }
  if (body.cadence !== undefined) {
    if (!isOpsObligationCadence(body.cadence)) return { ok: false, error: "cadence invalid" };
    patch.cadence = body.cadence;
  }
  if (body.status !== undefined) {
    if (!isOpsObligationStatus(body.status)) return { ok: false, error: "status invalid" };
    patch.status = body.status;
  }
  if (body.amount_cents !== undefined) {
    const cents = asOptionalCents(body.amount_cents);
    if (!cents.ok) return { ok: false, error: `amount_cents ${cents.error}` };
    patch.amount_cents = cents.value;
  }
  if (body.starts_on !== undefined) {
    const d = asOptionalDate(body.starts_on);
    if (!d.ok) return { ok: false, error: `starts_on ${d.error}` };
    patch.starts_on = d.value;
  }
  if (body.ends_on !== undefined) {
    const d = asOptionalDate(body.ends_on);
    if (!d.ok) return { ok: false, error: `ends_on ${d.error}` };
    patch.ends_on = d.value;
  }
  const textFields: Array<[keyof OpsObligationDraft, number]> = [
    ["account_ref", 120],
    ["description", 2000],
    ["due_rule", 200],
    ["responsible_party", 200],
    ["provider_contact", 500],
    ["notes", 8000],
  ];
  for (const [key, max] of textFields) {
    if (body[key] === undefined) continue;
    const t = asOptionalText(body[key], max);
    if (!t.ok) return { ok: false, error: `${key} ${t.error}` };
    (patch as Record<string, unknown>)[key] = t.value;
  }
  if (body.details !== undefined) {
    patch.details = parseObligationDetails(body.details);
  }
  if (!Object.keys(patch).length) return { ok: false, error: "no fields to patch" };
  return { ok: true, value: patch };
}
